import React, { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { FaArrowLeft, FaDownload, FaUpload } from "react-icons/fa";
import { useForm } from "react-hook-form";
import * as XLSX from "xlsx";

/* ---------- Shared UI tokens ---------- */
const baseInput =
  "w-full px-3 py-2.5 border rounded-lg text-[14px] focus:outline-none";
const okRing =
  "border-gray-300 focus:ring-4 focus:ring-[#9481ce]/20 focus:border-[#9481ce]";
const errRing =
  "border-red-400 focus:ring-4 focus:ring-red-200 focus:border-red-500";

const RfidMasterForm = ({
  title = "Create RFID Master",
  subtitle = "Add RFID to EPC mapping for this tenant.",
  defaultValues = {
    rfidCode: "",
    epcCode: "",
  },
  onSubmit,
  isLoading = false,
  submitLabel = "Submit",
  backTo = "/organisation/rfid-master/list",

  // optional bulk features
  showBulkActions = true,
  onBulkUpload,
  isBulkUploading = false,
}) => {
  const fileInputRef = useRef(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setFocus,
  } = useForm({
    defaultValues: {
      rfidCode: defaultValues?.rfidCode || "",
      epcCode: defaultValues?.epcCode || "",
    },
  });

  useEffect(() => {
    reset({
      rfidCode: defaultValues?.rfidCode || "",
      epcCode: defaultValues?.epcCode || "",
    });
  }, [defaultValues?.rfidCode, defaultValues?.epcCode, reset]);

  useEffect(() => {
    requestAnimationFrame(() =>
      setFocus("rfidCode", { shouldSelect: true }),
    );
  }, [setFocus]);

  const handleFormSubmit = (form) => {
    const payload = {
      rfidCode: (form.rfidCode || "").trim().toUpperCase(),
      epcCode: (form.epcCode || "").trim().toUpperCase(),
    };

    onSubmit?.(payload, { reset, setFocus });
  };

  const handleDownloadTemplate = () => {
    const rows = [
      { RFIDCode: "", EPCCode: "" },
      { RFIDCode: "RFID001", EPCCode: "E28068940000500A1B2C3D4E" },
      { RFIDCode: "RFID002", EPCCode: "E28068940000500A1B2C3D4F" },
    ];

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "RFID Master Template");
    XLSX.writeFile(wb, "RFID_Master_Template.xlsx");
  };

  const handlePickExcel = () => {
    fileInputRef.current?.click();
  };

  const handleExcelChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    try {
      await onBulkUpload?.(formData, file);
    } finally {
      e.target.value = "";
    }
  };

  return (
    <div className="px-4 py-4">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 px-2 py-2 flex items-center gap-3">
          <Link
            to={backTo}
            title="Back to RFID Master List"
            className="inline-flex items-center justify-center h-9 w-9 rounded-full border border-gray-200 hover:bg-gray-100 transition"
          >
            <FaArrowLeft className="text-[#6f5cc5] text-sm" />
          </Link>

          <div className="flex-1">
            <h1 className="text-xl md:text-2xl font-semibold text-[#4b3d91] leading-tight">
              {title}
            </h1>
            <p className="text-xs text-gray-500">{subtitle}</p>
          </div>
        </div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.28 }}
        className="max-w-4xl mx-auto bg-white shadow-md rounded-xl mt-4 border border-gray-200 relative"
      >
        {(isLoading || isBulkUploading) && (
          <div className="absolute inset-0 bg-white/60 backdrop-blur-[2px] flex items-center justify-center z-20 rounded-xl">
            <div className="animate-spin h-7 w-7 border-2 border-[#9481ce] border-t-transparent rounded-full" />
          </div>
        )}

        <div className="p-5 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div>
              <h2 className="text-sm font-semibold text-[#4b3d91]">
                Quick Actions
              </h2>
              <p className="text-xs text-gray-500">
                Download template, fill RFID and EPC values, then upload Excel.
              </p>
            </div>

            {showBulkActions && (
              <div className="flex flex-col sm:flex-row gap-2">
                <button
                  type="button"
                  onClick={handleDownloadTemplate}
                  className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-medium border border-gray-300 bg-white hover:bg-gray-50 text-gray-700"
                >
                  <FaDownload className="text-xs" />
                  Download Template
                </button>

                <button
                  type="button"
                  onClick={handlePickExcel}
                  disabled={isBulkUploading}
                  className="inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-medium shadow-sm bg-[#6f5cc5] hover:bg-[#5f4eb4] text-white disabled:opacity-60"
                >
                  <FaUpload className="text-xs" />
                  {isBulkUploading ? "Uploading..." : "Upload Excel"}
                </button>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls"
                  className="hidden"
                  onChange={handleExcelChange}
                />
              </div>
            )}
          </div>
        </div>

        <form className="p-5" onSubmit={handleSubmit(handleFormSubmit)}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-[13px] font-medium text-gray-700 mb-1">
                RFID Code <span className="text-red-500">*</span>
              </label>
              <input
                {...register("rfidCode", {
                  required: "RFID Code is required",
                })}
                placeholder="Enter RFID Code"
                className={`${baseInput} ${
                  errors.rfidCode ? errRing : okRing
                }`}
              />
              {errors.rfidCode?.message && (
                <p className="text-xs text-red-600 mt-1">
                  {errors.rfidCode.message}
                </p>
              )}
            </div>

            <div>
              <label className="block text-[13px] font-medium text-gray-700 mb-1">
                EPC Code <span className="text-red-500">*</span>
              </label>
              <input
                {...register("epcCode", {
                  required: "EPC Code is required",
                })}
                placeholder="Enter EPC Code"
                className={`${baseInput} ${
                  errors.epcCode ? errRing : okRing
                }`}
              />
              {errors.epcCode?.message && (
                <p className="text-xs text-red-600 mt-1">
                  {errors.epcCode.message}
                </p>
              )}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-2 pt-5">
            <motion.button
              whileHover={{ scale: isLoading ? 1 : 1.01 }}
              whileTap={{ scale: isLoading ? 1 : 0.98 }}
              type="submit"
              className="inline-flex justify-center items-center w-full sm:w-auto px-4 py-2.5 rounded-lg font-medium shadow-sm bg-[#9481ce] hover:bg-[#7d6ec0] text-white disabled:opacity-60"
              disabled={isLoading}
            >
              {isLoading ? "Saving..." : submitLabel}
            </motion.button>

            <Link
              to={backTo}
              className="inline-flex justify-center items-center w-full sm:w-auto px-4 py-2.5 rounded-lg font-medium border border-gray-300 bg-white hover:bg-gray-50 text-gray-700"
            >
              Cancel
            </Link>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default RfidMasterForm;